import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, SupportedLanguage } from '../lib/format';

export interface ClientPortfolioItem {
  company_id: number;
  company_name: string;
  headcount: number;
  pending_leaves: number;
  pending_approvals: number;
  monthly_payroll_cost: number;
  payroll_month: string | null;
  filing_status: string;
  posted_runs: number;
  pending_runs: number;
  latest_posted_run_id: number | null;
}

export interface DashboardHeadcountByDept {
  dept: string;
  count: number;
}

export interface DashboardPayrollTrendPoint {
  month: string;
  cost: number;
}

export interface DashboardMetrics {
  headcount: number;
  approved_positions: number;
  vacant_positions: number;
  pending_leaves: number;
  pending_approvals: number;
  monthly_payroll_cost: number;
  headcount_by_dept: DashboardHeadcountByDept[];
  payroll_trend_12m: DashboardPayrollTrendPoint[];
}

export interface WorkbookOptions {
  company_id?: number;
  payroll_month?: string | null;
}

export interface ClientPortfolioCardProps {
  clients: ClientPortfolioItem[];
  activeLanguage: SupportedLanguage;
  onFocusCompany: (companyId: number, anchor?: string) => void;
  onExportErca: (url: string, filename: string, options: WorkbookOptions) => void;
  onExportCostCenters: (url: string, filename: string, options: WorkbookOptions) => void;
  onExportGl: (url: string, filename: string, options: WorkbookOptions) => void;
  onExportBankEft: (url: string, filename: string, mime: string, options: WorkbookOptions) => void;
}

export function ClientPortfolioCard({
  clients,
  activeLanguage,
  onFocusCompany,
  onExportErca,
  onExportCostCenters,
  onExportGl,
  onExportBankEft,
}: ClientPortfolioCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="grid dashboard-grid">
      <article className="card list-card">
        <div className="section-header">
          <h2>Client portfolio</h2>
          <span className="helper">{clients.length}</span>
        </div>
        {clients.length ? (
          <ul className="list">
            {clients.map((client) => {
              const slug = client.company_name.replace(/\s+/g, '-').toLowerCase();
              return (
                <li key={client.company_id}>
                  <div>
                    <strong>{client.company_name}</strong>
                    <span>
                      {client.headcount} employees • {client.pending_leaves} pending leaves • {client.pending_approvals} pending approvals
                    </span>
                    <span>
                      {formatCurrency(client.monthly_payroll_cost, activeLanguage)} monthly payroll • {client.payroll_month || 'No payroll month'} • Filing {client.filing_status}
                    </span>
                    <span>
                      Posted runs {client.posted_runs} • Pending runs {client.pending_runs}
                    </span>
                  </div>
                  <div className="section-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => onFocusCompany(client.company_id)}
                    >
                      Open company
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() =>
                        onExportErca('/api/payroll/reports/erca.xlsx', `erca-${slug}.xlsx`, {
                          company_id: client.company_id,
                          payroll_month: client.payroll_month,
                        })
                      }
                      disabled={!client.payroll_month}
                    >
                      {t('exportErca')}
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() =>
                        onExportCostCenters('/api/payroll/reports/cost-centers.xlsx', `cost-centers-${slug}.xlsx`, {
                          company_id: client.company_id,
                          payroll_month: client.payroll_month,
                        })
                      }
                      disabled={!client.payroll_month}
                    >
                      {t('exportCostCenters')}
                    </button>
                    {client.latest_posted_run_id ? (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() =>
                          onExportGl(
                            `/api/payroll/runs/${client.latest_posted_run_id}/gl-export.xlsx`,
                            `gl-${slug}.xlsx`,
                            { company_id: client.company_id, payroll_month: undefined },
                          )
                        }
                      >
                        Export GL
                      </button>
                    ) : null}
                    {client.latest_posted_run_id ? (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() =>
                          onExportBankEft(
                            `/api/payroll/runs/${client.latest_posted_run_id}/bank-eft.txt`,
                            `bank-eft-${slug}.txt`,
                            'text/plain;charset=utf-8;',
                            { company_id: client.company_id, payroll_month: undefined },
                          )
                        }
                      >
                        Export bank EFT
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => onFocusCompany(client.company_id, 'audit-log-card')}
                    >
                      View audit logs
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>No assigned client companies yet.</p>
        )}
      </article>
    </section>
  );
}

export interface EnterpriseDashboardCardProps {
  metrics: DashboardMetrics;
  activeLanguage: SupportedLanguage;
}

export function EnterpriseDashboardCard({
  metrics,
  activeLanguage,
}: EnterpriseDashboardCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article className="card list-card">
      <div className="section-header">
        <h2>{t('enterpriseDashboard')}</h2>
        <span className="helper">{t('appTitle')}</span>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">{t('employees')}</span>
          <strong>{metrics.headcount}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('approvedPositions')}</span>
          <strong>{metrics.approved_positions}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('vacantPositions')}</span>
          <strong>{metrics.vacant_positions}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('pendingLeaves')}</span>
          <strong>{metrics.pending_leaves}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('pendingApprovals')}</span>
          <strong>{metrics.pending_approvals}</strong>
        </div>
        <div className="stat-card">
          <span className="stat-label">{t('monthlyPayrollCost')}</span>
          <strong>{formatCurrency(metrics.monthly_payroll_cost, activeLanguage)}</strong>
        </div>
      </div>

      <div className="split">
        <div>
          <h3>{t('headcountByDepartment')}</h3>
          {metrics.headcount_by_dept.length ? (
            <ul className="list compact-list">
              {metrics.headcount_by_dept.map((item) => (
                <li key={item.dept}>
                  <strong>{item.dept}</strong>
                  <span>{item.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{t('noEmployees')}</p>
          )}
        </div>
        <div>
          <h3>{t('payrollTrend')}</h3>
          <ul className="list compact-list">
            {metrics.payroll_trend_12m.map((point) => (
              <li key={point.month}>
                <strong>{point.month}</strong>
                <span>{formatCurrency(point.cost, activeLanguage)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}
